import { Checkbox, Grid, GridColumn, GridRow, Input, ModalBottomButtons } from '@bp/ui-components';
import { Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router-dom';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  SubstitutionAppFormType,
  SubstitutionAppTimeValues,
} from '../../../pages/Substitutions/Setup/SubstitutionAppSettings/SubstitutionAppSettingsPage';
import {
  useSaveSubstitutionAppConfigMutation,
  useSubstitutionAppConfigQuery,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';

type SubstitionAppAccessFormProps = {
  readonly?: boolean;
  onLoad?: (isLoading: boolean) => void;
  onClose?: () => void;
};

export const SubstitionAppAccessForm = ({ readonly, onLoad, onClose }: SubstitionAppAccessFormProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('SUBSTITUTION_APP_CONFIG');

  const [, save] = useSaveSubstitutionAppConfigMutation();

  const [{ data: appConfigData }] = useSubstitutionAppConfigQuery({ context });

  const appAccess = appConfigData?.substitutionAppConfig;

  const initialValues: SubstitutionAppFormType = {
    teacherPassword: appAccess?.teacherPassword ?? '',
    studentPassword: appAccess?.studentPassword ?? '',
    allowIndividualLogins: appAccess?.allowIndividualLogins ?? false,
    useTeacherShortname: appAccess?.useTeacherShortname ?? true,
    useClassShortname: appAccess?.useClassShortname ?? true,
    useRoomShortname: appAccess?.useRoomShortname ?? true,
    timeDisplayMode: (appAccess?.timeDisplayMode as SubstitutionAppTimeValues) ?? 'time',
    showClassGroups: appAccess?.showClassGroups ?? false,
    showOriginalSubject: appAccess?.showOriginalSubject ?? true,
    showCurrentSubject: appAccess?.showCurrentSubject ?? true,
    showHolidays: appAccess?.showHolidays ?? true,
  };

  async function handleSubmit(values: SubstitutionAppFormType, formHelpers: FormikHelpers<SubstitutionAppFormType>) {
    onLoad && onLoad(true);

    // Beide Formulare der App-Einstellungen schicken immer alle Felder; das Backend legt an oder ersetzt.
    const result = await save({ input: { ...values } }, context);

    if (result?.error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    onLoad && onLoad(false);
    formHelpers.resetForm();
    onClose && onClose();
  }

  return (
    <Formik<SubstitutionAppFormType> initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, submitForm }) => {
        return (
          <Form style={{ width: '100%' }}>
            <Grid useFormGap>
              <GridRow spacingBottom={'none'}>
                <GridColumn width={5}>
                  <Input
                    name={'teacher-user-readonly'}
                    label={t('substitutions.app.teacherUser')}
                    value={'lehrer'}
                    readonly
                  />
                </GridColumn>
                <GridColumn width={5}>
                  <Input
                    name={'student-user-readonly'}
                    label={t('substitutions.app.studentUser')}
                    value={'schüler'}
                    readonly
                  />
                </GridColumn>
                <GridColumn width={2} align='end'>
                  <Checkbox
                    className='mt-3'
                    name={readonly ? 'individual-logins-readonly' : 'individual-logins'}
                    label={t('substitutions.app.individualLogins')}
                    onChange={async (e) => {
                      await setFieldValue('allowIndividualLogins', e.target.checked);
                    }}
                    checked={values.allowIndividualLogins}
                    readonly={readonly}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop={'none'}>
                <GridColumn width={5}>
                  <Input
                    name={readonly ? 'teacher-password-readonly' : 'teacher-password'}
                    label={t('substitutions.app.teacherPassword')}
                    onChange={async (e) => {
                      await setFieldValue('teacherPassword', e.target.value);
                    }}
                    value={values.teacherPassword}
                    readonly={readonly}
                    className='mono-space-input'
                  />
                </GridColumn>
                <GridColumn width={5} offset='right'>
                  <Input
                    name={readonly ? 'student-password-readonly' : 'student-password'}
                    label={t('substitutions.app.studentPassword')}
                    onChange={async (e) => {
                      await setFieldValue('studentPassword', e.target.value);
                    }}
                    value={values.studentPassword}
                    readonly={readonly}
                    className='mono-space-input'
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            {!readonly && (
              <ModalBottomButtons
                closeButton={{
                  callback: () => {
                    onClose && onClose();
                    resetForm();
                  },
                }}
                submitButton={{
                  callback: async () => {
                    await submitForm();
                  },
                  disabled: isSubmitting || !dirty,
                }}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
